/**
 * ExtrasItemNew - MVVM-Compliant Component
 *
 * This component renders a content item using the new ContentViewModel.
 * It follows MVVM principles with proper separation of concerns:
 * - View only renders based on ViewModel state
 * - All business logic is in ViewModel
 * - Actions are dispatched through ViewModel callbacks
 */

import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import * as styles from './extras-item/extrasItem.css';
import { ReactNode } from 'react';
import { Typography } from '@design-system/components/typography/typography';
import { Col, Row } from '@design-system/components/grid/grid';
import {
  faCheck,
  faRefresh,
  faPlay,
  faThumbsUp,
  faComment,
} from '@fortawesome/pro-regular-svg-icons';
import { Pill } from '@client/new/pages/promotions/view/components/Pill';
import { IconButton } from '@design-system/components/button/iconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { Badge } from '@design-system/components/badge';
import { CellMenu } from '@client/new/components/TableShared/Cell';
import { faEyeSlash, faPlus } from '@fortawesome/pro-solid-svg-icons';
import {
  AccessMethodItem,
  ContentViewModel,
} from '../viewModel/extras.viewModel.types';
import { AccessMethodsList } from './AccessMethodsList';
import { IconMenu } from './IconMenu';
import { color } from '@design-system/tokens';
import { F1 } from '@client/new/helpers/types';

const CustomButton = ({
  isOpen,
  onClick,
}: {
  isOpen: boolean;
  onClick: F1<React.MouseEvent>;
}) => {
  console.log('isOpen', isOpen);
  return (
    <Row alignCenter justifyCenter>
      <button
        style={{
          width: 16,
          height: 16,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 4,
          border: 'none',
          cursor: 'pointer',
          background: `rgb(${isOpen ? color['P-100'] : color['P-20']})`,
        }}
        onClick={onClick}
      >
        <FontAwesomeIcon
          icon={faPlus}
          style={{
            height: '9px',
            color: `rgb(${isOpen ? color['P-00'] : color['P-100']})`,
          }}
        />
      </button>
    </Row>
  );
};
/**
 * Convert ActionItem to IMenuItem for CellMenu
 */
const toMenuItem = (action: ContentViewModel['dropdownActions'][number]) => ({
  text: action.label,
  onClick: action.onClick,
  disabled: action.disabled,
  secondaryText: action.disabledReason,
  color: action.isDestructive ? ('error' as const) : undefined,
});

export type ExtrasItemNewProps = {
  viewModel: ContentViewModel;
  imageURL?: string;
  description: ReactNode;
  dndProps: DraggableProvidedDragHandleProps | undefined | null;
  onEdit: () => void;
};

export const ExtrasItemNew = (p: ExtrasItemNewProps) => {
  const vm = p.viewModel;
  const isPublished = vm.publicationStatus === 'published';

  return (
    <Row
      id={vm.id}
      className={styles.wrapper({ unpublished: !isPublished })}
      alignCenter
      onClick={() => p.onEdit()}
      {...p.dndProps}
    >
      {/* Image with unpublished overlay */}
      <div className={styles.imageWrapper}>
        <Col className={styles.imageContainer({ unpublished: !isPublished })}>
          {vm.imageUrl ? (
            <img src={vm.imageUrl} alt={vm.title} className={styles.image} />
          ) : (
            <div className={styles.placeholder}>
              <FontAwesomeIcon
                icon={faPlus}
                color="#5F1ECC"
                className={styles.placeholderIcon}
              />
            </div>
          )}
        </Col>
        {!isPublished && (
          <div className={styles.unpublishedOverlay}>
            <div className={styles.overlayBackground}>
              <FontAwesomeIcon
                icon={faEyeSlash}
                className={styles.overlayIcon}
              />
            </div>
          </div>
        )}
      </div>

      {/* Title and description */}
      <Col className={styles.textWrapper({ unpublished: !isPublished })}>
        <Typography
          variant="body"
          size="large"
          fontWeight="normal"
          className={styles.title}
        >
          {vm.title}
        </Typography>

        <Typography
          variant="body"
          size="small"
          fontWeight="normal"
          className={styles.description}
        >
          {p.description}
        </Typography>

        {/* Video stats */}
        {vm.videoDetails && (
          <Typography variant="body" size="small" fontWeight="normal">
            <Row gap="s" alignCenter>
              <FontAwesomeIcon icon={faPlay} />
              <span>{vm.videoDetails.watchCount}</span>
              <span>•</span>
              <FontAwesomeIcon icon={faThumbsUp} />
              <span>{vm.videoDetails.likesCount}</span>
              <span>•</span>
              <Row
                gap="s"
                alignCenter
                onClick={(e) => {
                  e.stopPropagation();
                  vm.videoDetails?.onCommentsClick();
                }}
                className={styles.videoCommentsClickable}
              >
                <FontAwesomeIcon
                  icon={faComment}
                  className={styles.videoCommentsIcon}
                />
                <Typography
                  variant="body"
                  size="small"
                  className={styles.videoCommentsText}
                >
                  {vm.videoDetails.commentsCount}
                </Typography>
              </Row>
            </Row>
          </Typography>
        )}
      </Col>

      {/* Purchase display column */}
      <Col className={styles.offersColumn}>
        <Row gap="m" alignCenter style={{ width: 'max-content' }}>
          <AccessMethodsList
            items={vm.accessMethodItems}
            renderItem={(item: AccessMethodItem, index: number): ReactNode => {
              if (item.type === 'label') {
                return (
                  <Typography
                    key={`label-${index}`}
                    variant="body"
                    size="small"
                  >
                    {item.text}
                  </Typography>
                );
              }

              return (
                <Row key={item.id} gap="s" alignCenter>
                  <div
                    className={styles.offerDot({
                      published: item.status === 'published',
                    })}
                  />
                  <Typography
                    variant="body"
                    size="small"
                    className={styles.offerName}
                  >
                    {item.title}
                  </Typography>
                </Row>
              );
            }}
          />
          {/* Access method menu - icon dropdown */}
          {vm.accessMethodMenu && (
            <IconMenu
              buttonComponent={CustomButton}
              items={vm.accessMethodMenu.items.map(toMenuItem)}
            />
          )}
        </Row>
      </Col>

      {/* Type badge / Processing indicator */}
      <Col>
        {vm.isProcessing ? (
          <Tooltip
            arrow
            tooltipProps={{
              maxWidth: 177,
            }}
            variant="dark"
            title="Videos are ready to publish when they have finished processing."
          >
            <Badge
              iconClassName={styles.spinningIcon}
              label="Processing…"
              startIcon={faRefresh}
            />
          </Tooltip>
        ) : (
          <Pill type="Inactive" content={vm.typeLabel} />
        )}
      </Col>

      {/* Primary action (Publish button or Archived label) */}
      <div className="ml-auto">
        {vm.primaryAction === 'archived-label' && (
          <Typography
            variant="body"
            size="medium"
            className={styles.archivedLabel}
          >
            Archived
          </Typography>
        )}

        {vm.primaryAction === 'publish-button' && vm.publishButton && (
          <>
            {vm.publishButton.isPublished ? (
              <IconButton
                disabled={vm.publishButton.disabled || vm.publishButton.loading}
                loading={vm.publishButton.loading}
                icon={faCheck}
                variant="filled"
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  vm.publishButton?.onClick();
                }}
              >
                {vm.publishButton.label}
              </IconButton>
            ) : (
              <IconButton
                disabled={vm.publishButton.disabled || vm.publishButton.loading}
                loading={vm.publishButton.loading}
                variant="filled"
                color="primaryTonal"
                onClick={(e) => {
                  e.stopPropagation();
                  vm.publishButton?.onClick();
                }}
              >
                {vm.publishButton.label}
              </IconButton>
            )}
          </>
        )}
      </div>

      {/* Dropdown menu (Archive, Delete) */}
      <CellMenu items={vm.dropdownActions.map(toMenuItem)} />
    </Row>
  );
};
